import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MatchReport from './MatchReport';
import MatchingModal from './MatchingModal';
import {
  X, Upload, FileText, Check, ChevronRight, Loader2,
  AlertCircle, Eye
} from 'lucide-react';

const STEPS = ['Your Details', 'Match Report', 'Review & Submit'];

const ApplyModal = ({ position, userProfile, onClose, onSuccess }) => {
  const [step, setStep] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [matchReport, setMatchReport] = useState(null);
  const [error, setError] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeUrl, setResumeUrl] = useState('');
  const [additionalDocs, setAdditionalDocs] = useState([]);
  const [uploadedDocUrls, setUploadedDocUrls] = useState([]);
  const [prospectProfile, setProspectProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [showReportViewer, setShowReportViewer] = useState(false);
  const [showCvViewer, setShowCvViewer] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    locationCity: '', noticePeriod: '', salaryExpectation: '',
    coverLetter: '', linkedinProfile: ''
  });

  useEffect(() => { fetchProspectProfile(); }, []);

  const fetchProspectProfile = async () => {
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/prospects/user/${userProfile.id}`
      );
      if (resp.data.success) {
        const p = resp.data.data;
        setProspectProfile(p);
        setFormData({
          firstName: p.firstName || userProfile.firstName || '',
          lastName: p.lastName || userProfile.lastName || '',
          email: p.email || userProfile.email || '',
          phone: p.phone || '',
          locationCity: p.location || '',
          noticePeriod: p.availability || '',
          salaryExpectation: p.expectedSalaryMin ? String(p.expectedSalaryMin) : '',
          coverLetter: '', linkedinProfile: ''
        });
        if (p.resumeUrl) setResumeUrl(p.resumeUrl);
      }
    } catch {
      setFormData(prev => ({
        ...prev,
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        email: userProfile.email || ''
      }));
    } finally { setLoadingProfile(false); }
  };

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { setError('File size must be less than 10MB'); return; }
      setResumeFile(file);
      setResumeUrl('');
      setError('');
    }
  };

  const isFormValid = () => {
    return formData.firstName && formData.lastName && formData.email &&
      formData.phone && formData.locationCity && formData.noticePeriod &&
      formData.salaryExpectation && (resumeFile || resumeUrl);
  };

  const handleAnalyze = async () => {
    if (!isFormValid()) { setError('Please fill in all required fields and ensure you have a CV'); return; }
    setAnalyzing(true);
    setError('');

    try {
      let fileForMatch = resumeFile;
      let uploadedResumeUrl = resumeUrl;

      if (resumeFile) {
        const uploadForm = new FormData();
        uploadForm.append('resume', resumeFile);
        const uploadResp = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/public/upload-resume`,
          uploadForm, { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        if (!uploadResp.data.success) throw new Error('Resume upload failed');
        uploadedResumeUrl = uploadResp.data.data.resumeUrl;
        setResumeUrl(uploadedResumeUrl);
      } else if (resumeUrl) {
        try {
          const resp = await axios.get(resumeUrl, { responseType: 'blob' });
          fileForMatch = new File([resp.data], 'resume.pdf', { type: resp.data.type || 'application/pdf' });
        } catch {
          setError('Could not access your existing CV. Please upload a new one.');
          setAnalyzing(false);
          return;
        }
      }

      // Upload additional documents
      const docUrls = [];
      for (const doc of additionalDocs) {
        const docForm = new FormData();
        docForm.append('resume', doc);
        const docResp = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/public/upload-resume`,
          docForm,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        if (docResp.data.success) {
          docUrls.push({
            name: doc.name,
            url: docResp.data.data.resumeUrl,
            size: doc.size,
          });
        }
      }
      setUploadedDocUrls(docUrls);

      const matchForm = new FormData();
      matchForm.append('resume', fileForMatch);
      matchForm.append('position_data', JSON.stringify({
        title: position.title,
        must_have_skills: position.must_have_skills || [],
        nice_to_have_skills: position.nice_to_have_skills || [],
        description: position.description || '',
        responsibilities: position.responsibilities || '',
        profile_requirements: position.profile_requirements || ''
      }));
      additionalDocs.forEach(doc => {
        matchForm.append('additional_docs', doc);
      });

      const matchResp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/match-score`,
        matchForm, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );

      if (matchResp.data.success) {
        setMatchReport(matchResp.data.data);
        setStep(1);
      } else {
        throw new Error(matchResp.data.message || 'Match scoring failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Something went wrong.');
    } finally { setAnalyzing(false); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions/${position.id}/apply`,
        {
          prospectId: userProfile.id,
          coverLetter: formData.coverLetter,
          match_report: matchReport ? JSON.stringify(matchReport) : null,
          match_score: matchReport?.score || null,
          resume_url: resumeUrl,
          notice_period: formData.noticePeriod,
          salary_expectation: formData.salaryExpectation,
          linkedin_profile: formData.linkedinProfile,
          location_city: formData.locationCity,
          phone: formData.phone,
          additional_documents: uploadedDocUrls
        }
      );
      if (response.data.success) { onSuccess(response.data.data); }
      else { throw new Error(response.data.message); }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="apply-modal-overlay">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" data-testid="apply-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900" data-testid="apply-modal-title">Apply for {position.title}</h2>
            <p className="text-sm text-gray-500">{position.company_name || position.organizations?.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg" data-testid="apply-modal-close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 px-6 py-3 bg-white border-b border-gray-100">
          {STEPS.map((label, i) => (
            <React.Fragment key={i}>
              <div className={`flex items-center gap-2 ${i <= step ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${
                  i < step ? 'bg-blue-600 text-white border-blue-600' :
                  i === step ? 'border-blue-600 text-blue-600' : 'border-gray-300 text-gray-400'
                }`}>
                  {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span className="text-xs font-medium hidden sm:inline">{label}</span>
              </div>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5 flex items-start gap-2" data-testid="apply-modal-error">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {loadingProfile ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-500">Loading your profile...</span>
            </div>
          ) : (
            <>
              {/* Step 0: Form */}
              {step === 0 && (
                <div className="space-y-5" data-testid="apply-step-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                      <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        data-testid="apply-modal-first-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                      <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        data-testid="apply-modal-last-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        data-testid="apply-modal-email" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="+49 123 456 789" data-testid="apply-modal-phone" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location (City) *</label>
                      <input type="text" name="locationCity" value={formData.locationCity} onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="e.g., Berlin" data-testid="apply-modal-location" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notice Period *</label>
                      <select name="noticePeriod" value={formData.noticePeriod} onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        data-testid="apply-modal-notice">
                        <option value="">Select...</option>
                        <option value="Immediate">Immediate</option>
                        <option value="2 weeks">2 weeks</option>
                        <option value="1 month">1 month</option>
                        <option value="2 months">2 months</option>
                        <option value="3 months">3 months</option>
                        <option value="6 months">6 months</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Salary Expectation (Annual, EUR) *</label>
                      <input type="number" name="salaryExpectation" value={formData.salaryExpectation} onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="e.g., 65000" data-testid="apply-modal-salary" />
                    </div>
                  </div>

                  {/* Resume / CV */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Resume / CV *</label>
                    {resumeUrl && !resumeFile ? (
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText className="w-6 h-6 text-blue-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Existing CV from your profile</p>
                              <p className="text-xs text-gray-500">{prospectProfile?.resumeFilename || 'resume.pdf'}</p>
                            </div>
                          </div>
                          <label className="text-sm text-blue-600 hover:text-blue-700 font-medium cursor-pointer" data-testid="apply-modal-change-cv">
                            Change
                            <input type="file" accept=".pdf,.doc,.docx,.txt,.rtf" onChange={handleFileChange} className="hidden" />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-5 text-center hover:border-blue-400 transition-colors">
                        {resumeFile ? (
                          <div className="flex items-center justify-center gap-3">
                            <FileText className="w-7 h-7 text-blue-600" />
                            <div className="text-left">
                              <p className="font-medium text-gray-900 text-sm">{resumeFile.name}</p>
                              <p className="text-xs text-gray-500">{(resumeFile.size / 1024).toFixed(1)} KB</p>
                            </div>
                            <button onClick={() => { setResumeFile(null); if (prospectProfile?.resumeUrl) setResumeUrl(prospectProfile.resumeUrl); }}
                              className="text-red-500 hover:text-red-700 text-sm font-medium ml-2" data-testid="apply-modal-remove-cv">Remove</button>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <Upload className="w-7 h-7 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600"><span className="text-blue-600 font-medium">Click to upload</span> or drag and drop</p>
                            <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX (max 10MB)</p>
                            <input type="file" accept=".pdf,.doc,.docx,.txt,.rtf" onChange={handleFileChange} className="hidden" data-testid="apply-modal-upload-cv" />
                          </label>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Additional Documents */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Documents <span className="text-gray-400">(optional, up to 4)</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">Certificates, portfolios, references, or other supporting documents. Included in AI match evaluation.</p>
                    <div className="space-y-2">
                      {additionalDocs.map((doc, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
                          <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{doc.name}</p>
                            <p className="text-xs text-gray-400">{(doc.size / 1024).toFixed(1)} KB</p>
                          </div>
                          <button
                            onClick={() => setAdditionalDocs(prev => prev.filter((_, i) => i !== idx))}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            data-testid={`modal-remove-doc-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {additionalDocs.length < 4 && (
                        <label className="flex items-center justify-center gap-2 border border-dashed border-gray-300 rounded-lg py-3 px-4 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
                          <Upload className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-500">Add document</span>
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.txt,.rtf,.png,.jpg,.jpeg"
                            className="hidden"
                            data-testid="apply-modal-additional-doc-input"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.size > 10 * 1024 * 1024) {
                                  setError('Each file must be less than 10MB');
                                  return;
                                }
                                setAdditionalDocs(prev => [...prev, file]);
                                setError('');
                              }
                              e.target.value = '';
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter <span className="text-gray-400">(optional)</span></label>
                    <textarea name="coverLetter" value={formData.coverLetter} onChange={handleInputChange} rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Tell us why you're interested in this role..." data-testid="apply-modal-cover-letter" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Profile <span className="text-gray-400">(optional)</span></label>
                    <input type="url" name="linkedinProfile" value={formData.linkedinProfile} onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="https://linkedin.com/in/..." data-testid="apply-modal-linkedin" />
                  </div>
                </div>
              )}

              {/* Step 1: Match Report */}
              {step === 1 && matchReport && (
                <div data-testid="apply-step-report">
                  <MatchReport
                    data={matchReport}
                    candidateName={`${formData.firstName} ${formData.lastName}`}
                    candidateInfo={{
                      noticePeriod: formData.noticePeriod,
                      salaryExpectation: formData.salaryExpectation,
                      locationCity: formData.locationCity
                    }}
                  />
                </div>
              )}

              {/* Step 2: Review & Submit */}
              {step === 2 && (
                <div className="space-y-5" data-testid="apply-step-review">
                  <div className="bg-white rounded-lg border border-gray-200 p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-5">Review Your Application</h3>

                    {/* Position */}
                    <div className="mb-5 pb-5 border-b border-gray-200">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Position</h4>
                      <p className="text-base font-medium text-gray-900">{position.title}</p>
                      <p className="text-sm text-gray-600">{position.company_name || position.organizations?.name} &middot; {position.location}</p>
                    </div>

                    {/* Personal Info */}
                    <div className="mb-5 pb-5 border-b border-gray-200">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Personal Information</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-gray-500">Name:</span> <span className="font-medium">{formData.firstName} {formData.lastName}</span></div>
                        <div><span className="text-gray-500">Email:</span> <span className="font-medium">{formData.email}</span></div>
                        <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{formData.phone}</span></div>
                        <div><span className="text-gray-500">Location:</span> <span className="font-medium">{formData.locationCity}</span></div>
                        <div><span className="text-gray-500">Notice Period:</span> <span className="font-medium">{formData.noticePeriod}</span></div>
                        <div><span className="text-gray-500">Salary:</span> <span className="font-medium">EUR {parseInt(formData.salaryExpectation || '0').toLocaleString()}</span></div>
                      </div>
                    </div>

                    {/* Documents */}
                    <div className="mb-5 pb-5 border-b border-gray-200">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Documents</h4>
                      <div className="space-y-2">
                        {/* CV */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-blue-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{resumeFile?.name || prospectProfile?.resumeFilename || 'CV'}</p>
                              <p className="text-xs text-gray-500">Resume / CV</p>
                            </div>
                          </div>
                          {resumeUrl && (
                            <button onClick={() => setShowCvViewer(true)}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                              data-testid="view-cv-btn">
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                          )}
                        </div>
                        {/* Match Report */}
                        {matchReport && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-2">
                              <FileText className="w-5 h-5 text-indigo-600" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">AI Match Report</p>
                                <p className="text-xs text-gray-500">{matchReport.score}% — {matchReport.verdict}</p>
                              </div>
                            </div>
                            <button onClick={() => setShowReportViewer(true)}
                              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                              data-testid="view-report-btn">
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Cover Letter */}
                    {formData.coverLetter && (
                      <div className="mb-5 pb-5 border-b border-gray-200">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cover Letter</h4>
                        <p className="text-sm text-gray-700 whitespace-pre-line">{formData.coverLetter}</p>
                      </div>
                    )}

                    {formData.linkedinProfile && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">LinkedIn</h4>
                        <a href={formData.linkedinProfile} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-sm">{formData.linkedinProfile}</a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center gap-3">
          {step === 0 && (
            <>
              <button onClick={onClose} className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 font-medium text-sm" data-testid="apply-modal-cancel">Cancel</button>
              <button onClick={handleAnalyze} disabled={!isFormValid() || analyzing}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="apply-modal-analyze-btn">
                {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing your profile...</> : 'Analyze & Continue'}
              </button>
            </>
          )}
          {step === 1 && (
            <>
              <button onClick={() => setStep(0)} className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 font-medium text-sm">Back</button>
              <button onClick={() => setStep(2)} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm" data-testid="apply-modal-continue-review">
                Continue to Review
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button onClick={() => setStep(1)} className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 font-medium text-sm">Back</button>
              <button onClick={handleSubmit} disabled={submitting}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm disabled:bg-gray-400 flex items-center justify-center gap-2"
                data-testid="apply-modal-submit-btn">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : 'Submit Application'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* CV Viewer Modal */}
      {showCvViewer && resumeUrl && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-6">
          <div className="bg-white rounded-xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-semibold text-gray-900">CV / Resume</h3>
              <button onClick={() => setShowCvViewer(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <iframe src={resumeUrl} className="flex-1 w-full" title="CV Viewer" />
          </div>
        </div>
      )}

      {/* Report Viewer Modal */}
      {showReportViewer && matchReport && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-6">
          <div className="bg-white rounded-xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-semibold text-gray-900">AI Match Report</h3>
              <button onClick={() => setShowReportViewer(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto bg-white">
              <MatchReport
                data={matchReport}
                candidateName={`${formData.firstName} ${formData.lastName}`}
                candidateInfo={{
                  noticePeriod: formData.noticePeriod,
                  salaryExpectation: formData.salaryExpectation,
                  locationCity: formData.locationCity
                }}
              />
            </div>
          </div>
        </div>
      )}
      {/* Matching Modal */}
      <MatchingModal active={analyzing} />
    </div>
  );
};

export default ApplyModal;
